import type { CandidateResponse } from 'clients/CampaignsClient/CandidateResponse';
import type { default as Candidate } from 'model/Candidate';
import type { UseActionsHookReturnType } from 'shared/legacy/contexts/CandidateContext/types';
import type { CampaignJobboard } from 'shared/legacy/contexts/SelectedCampaignContext/SelectedCampaignContext.types';
import type { CandidateStatus } from 'types/candidate';

export enum CandidateDetailsTab {
  CV = 'cv',
  details = 'details',
  activity = 'activity',
}

export type CandidateDetailsConfig = {
  initialTab: CandidateDetailsTab;
};

export type CandidateConfig = {
  candidateDetails: CandidateDetailsConfig;
};

export type SearchV3CandidateUIState = {
  isSelected: boolean;
  isLoading: boolean;
  status: CandidateStatus;
};

export type SearchV3CandidateResponse = Pick<Candidate, 'key' | 'name' | 'es_person_id'> & {
  select: (config?: DeepPartial<CandidateConfig>) => void;
  bindMatchesAndGetJobboards: (matches: CandidateMatches) => CandidateJobboardsWithActiveState[];

  experiences: CandidateExperience[];
  preferences: CandidatePreferences[];
  education?: CandidateEducation[];
  location?: CandidateLocation;
  lastRefresh?: string;
  distanceToCommute?: CandidateResponse['distance_to_commute'];
  matchingRate?: CandidateResponse['matching_rate'];
  robinVerified?: boolean;
  robinVerifiedMetadata?: {
    verified_at?: string;
    jobs?: string;
    working_hours_per_week?: string;
    available_days?: string;
    shifts?: string;
    salary?: string;
    travel_time?: string;
    transportation?: string;
  };
  robinVerifiedAt?: string;
  /** True when candidate qualifies for Robin Verified (API flag + verified_at within 168h). */
  robinVerifiedQualified?: boolean;
  campaignId?: number;
};

export type SearchV3CandidateStatefulResponse = {
  config: CandidateConfig;
  candidateUIState: SearchV3CandidateUIState;
  candidate: SearchV3CandidateResponse;
};

export type Searchv3CandidateHandler = (
  candidate: Candidate | undefined,
  config: { actions: UseActionsHookReturnType; config?: CandidateConfig },
) => SearchV3CandidateStatefulResponse;

/**
 * Types mapped for frontend use
 */

export type CandidateLocation = { name: string; distance?: string };

export type CandidatePreferences = {
  id: number;
  text: string;
  meta: string;
  robinVerified?: boolean;
  verifiedAt?: string;
};

export type CandidateExperience = {
  position: string;
  company?: string;
  date: string;
  description?: string;
  timeInMonths?: string;
};

export type CandidateEducation = {
  field: string;
  school: string;
  date: string;
  description?: string;
  fullDuration?: string | null;
};

export type CandidateMatches = Record<string, string[] | undefined>;

export type CandidateJobboardsWithActiveState = [CampaignJobboard, boolean];
